import { useEffect, useRef, useState, useCallback } from "react";

interface AdaptiveVideoLayoutOptions {
  margin?: number;
  aspectRatio?: number; // height / width ratio (e.g., 9/16 for 16:9)
  defaultAspectRatio?: "4:3" | "16:9" | "1:1" | "1:2";
}

interface CameraDimensions {
  width: number;
  height: number;
}

/**
 * Custom hook that implements the adaptive video layout algorithm
 * Calculates optimal sizes for video elements to fit in the window
 * while maintaining aspect ratio and respecting margins
 */
export function useAdaptiveVideoLayout(
  cameraCount: number,
  options: AdaptiveVideoLayoutOptions = {}
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<CameraDimensions[]>([]);

  const margin = options.margin ?? 10;
  const aspectRatio =
    options.aspectRatio ??
    (() => {
      // Calculate aspect ratio from string format (e.g., "16:9")
      const ratio = options.defaultAspectRatio ?? "16:9";
      const [width, height] = ratio.split(":").map(Number) as [number, number];
      return height / width;
    })();

  // Resize function that calculates optimal dimensions
  const resize = useCallback(() => {
    const containerWidth = window.innerWidth - margin * 2;
    const containerHeight = window.innerHeight - margin * 2;

    if (containerWidth <= 0 || containerHeight <= 0) return;

    const calculateArea = (
      width: number,
      height: number,
      increment: number
    ): number | false => {
      let i = 0;
      let w = 0;
      let h = increment * aspectRatio + margin * 2;

      while (i < cameraCount) {
        if (w + increment > width) {
          w = 0;
          h = h + increment * aspectRatio + margin * 2;
        }
        w = w + increment + margin * 2;
        i++;
      }

      if (h > height || increment > width) {
        return false;
      }
      return increment;
    };

    // Find the maximum size that fits all cameras
    let max = 0;
    let i = 1;
    while (i < 5000) {
      const area = calculateArea(containerWidth, containerHeight, i);
      if (area === false) {
        max = i - 1;
        break;
      }
      i++;
    }

    // Remove margins from max
    max = max - margin * 2;

    // Ensure max is positive
    if (max <= 0) {
      setDimensions([]);
      return;
    }

    // Calculate dimensions for all cameras
    const cameraDimensions: CameraDimensions[] = Array(cameraCount)
      .fill(null)
      .map(() => ({
        width: max,
        height: max * aspectRatio,
      }));

    setDimensions(cameraDimensions);
  }, [cameraCount, margin, aspectRatio]);

  // Initial resize and window resize event listener with debouncing
  useEffect(() => {
    // Debounce timer ref to cancel pending calculations
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const DEBOUNCE_DELAY = 150; // ms to wait after resize ends

    // Debounced resize function - only calculates after resizing has stopped
    const debouncedResize = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        resize();
      }, DEBOUNCE_DELAY);
    };

    // Initial resize after DOM is ready
    const timeoutId = setTimeout(() => {
      resize();
    }, 0);

    window.addEventListener("resize", debouncedResize);

    return () => {
      clearTimeout(timeoutId);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      window.removeEventListener("resize", debouncedResize);
    };
  }, [resize]);

  return {
    containerRef,
    dimensions,
    margin,
  };
}

